import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Trash2, ClipboardCheck, Link2, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESULT_COLORS = { pass: 'default', fail: 'destructive', conditional: 'outline' };

const AssetInspections = ({ asset, isAdmin, onRefresh, config = {}, employees = [], navigateTo, onClose }) => {
  const { t } = useTranslation();
  const { inspectionTypes = [] } = config;
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingInsp, setEditingInsp] = useState(null);
  const [audits, setAudits] = useState([]);
  const emptyForm = { inspection_type: 'Routine', date: '', result: 'pass', notes: '', inspector_name: '', iso_audit_id: '', next_due_date: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (addOpen || editOpen) {
      axios.get(`${API}/iso/audits`).then(r => setAudits(r.data)).catch(() => {});
    }
  }, [addOpen, editOpen]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error(t('inv.common.dateRequired')); return; }
    try {
      await axios.post(`${API}/inventory/assets/${asset.id}/inspections`, { ...form, iso_audit_id: form.iso_audit_id || null });
      toast.success(t('inv.inspections.recorded'));
      setAddOpen(false);
      setForm(emptyForm);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (insp) => {
    setEditingInsp(insp);
    setForm({
      inspection_type: insp.inspection_type || 'Routine',
      date: insp.date || '',
      result: insp.result || 'pass',
      notes: insp.notes || '',
      inspector_name: insp.inspector_name || '',
      iso_audit_id: insp.iso_audit_id || '',
      next_due_date: insp.next_due_date || '',
    });
    setEditOpen(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error(t('inv.common.dateRequired')); return; }
    try {
      await axios.put(`${API}/inventory/assets/${asset.id}/inspections/${editingInsp.id}`, { ...form, iso_audit_id: form.iso_audit_id || null });
      toast.success(t('inv.inspections.updated'));
      setEditOpen(false);
      setEditingInsp(null);
      setForm(emptyForm);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (inspectionId) => {
    try {
      await axios.delete(`${API}/inventory/assets/${asset.id}/inspections/${inspectionId}`);
      toast.success(t('inv.inspections.deleted'));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const inspections = asset.inspections || [];
  const typeNames = inspectionTypes.length > 0 ? inspectionTypes.map(t => t.name) : ['Routine', 'Safety', 'PAT Test', 'MOT'];

  const renderForm = (onSubmit, title, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">{t('inv.common.date')} *</Label><Input type="date" value={form.date} onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value  }))} required data-testid="inspection-date" /></div>
        <div>
          <Label className="text-xs">{t('inv.common.type')}</Label>
          <Select value={form.inspection_type} onValueChange={(v) => setForm(prev => ({ ...prev, inspection_type: v  }))}>
            <SelectTrigger data-testid="inspection-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{typeNames.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t('inv.inspections.result')}</Label>
          <Select value={form.result} onValueChange={(v) => setForm(prev => ({ ...prev, result: v  }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="pass">{t('inv.inspections.pass')}</SelectItem><SelectItem value="fail">{t('inv.inspections.fail')}</SelectItem><SelectItem value="conditional">{t('inv.inspections.conditional')}</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t('inv.inspections.inspector')}</Label>
          <Select value={form.inspector_name} onValueChange={(v) => setForm(prev => ({ ...prev, inspector_name: v  }))}>
            <SelectTrigger data-testid="inspection-inspector"><SelectValue placeholder={t('inv.common.select')} /></SelectTrigger>
            <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{t('inv.inspections.nextDueDate')}</Label><Input type="date" value={form.next_due_date} onChange={(e) => setForm(prev => ({ ...prev, next_due_date: e.target.value  }))} /></div>
        <div>
          <Label className="text-xs">{t('inv.inspections.isoAudit')}</Label>
          <Select value={form.iso_audit_id || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, iso_audit_id: v === 'none' ? '' : v  }))}>
            <SelectTrigger data-testid="inspection-iso-audit"><SelectValue placeholder={t('common.none')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('common.none')}</SelectItem>
              {audits.map(a => <SelectItem key={a.id} value={a.id}>{a.title || a.audit_number || a.id.slice(0, 8)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><Label className="text-xs">{t('common.notes')}</Label><Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value  }))} rows={2} /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={() => { setAddOpen(false); setEditOpen(false); }} data-testid="btn-cancel">{t('common.cancel')}</Button>
        <Button type="submit" data-testid="save-inspection-btn">{submitLabel}</Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-3" data-testid="asset-inspections">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h4 className="text-sm font-semibold">{t('inv.inspections.title', { count: inspections.length })}</h4>
          {asset.next_inspection_date && <p className="text-xs text-muted-foreground">{t('inv.inspections.nextDue')}: <span className="font-medium">{asset.next_inspection_date}</span></p>}
        </div>
        {isAdmin && <Button size="sm" onClick={() => { setForm(emptyForm); setAddOpen(true); }} data-testid="add-inspection-btn"><Plus className="w-4 h-4 mr-1" /> {t('inv.inspections.log')}</Button>}
      </div>

      {inspections.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">{t('inv.inspections.empty')}</p>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t('inv.common.date')}</TableHead><TableHead>{t('inv.common.type')}</TableHead><TableHead>{t('inv.inspections.result')}</TableHead>
            <TableHead>{t('inv.inspections.inspector')}</TableHead><TableHead>{t('inv.inspections.nextDue')}</TableHead><TableHead>{t('common.notes')}</TableHead>
            <TableHead>{t('auto.inv.iso')}</TableHead>{isAdmin && <TableHead className="w-[70px]" />}
          </TableRow></TableHeader>
          <TableBody>
            {inspections.map((insp) => (
              <TableRow key={insp.id} data-testid={`inspection-row-${insp.id}`}>
                <TableCell className="text-sm font-medium">{insp.date}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{insp.inspection_type}</Badge></TableCell>
                <TableCell><Badge variant={RESULT_COLORS[insp.result] || 'secondary'} className="text-xs">{t(`inv.inspections.${insp.result}`, { defaultValue: insp.result })}</Badge></TableCell>
                <TableCell className="text-sm">
                  {insp.inspector_name ? (
                    navigateTo ? (
                      <button className="text-blue-600 hover:underline text-sm" onClick={() => { onClose?.(); navigateTo('employees', { searchEmployee: insp.inspector_name }); }} data-testid={`nav-inspector-${insp.id}`}>{insp.inspector_name}</button>
                    ) : insp.inspector_name
                  ) : '-'}
                </TableCell>
                <TableCell className="text-sm">{insp.next_due_date || '-'}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">{insp.notes || '-'}</TableCell>
                <TableCell>
                  {insp.iso_audit_id ? (
                    navigateTo ? (
                      <button onClick={() => { onClose?.(); navigateTo('iso9001', { auditId: insp.iso_audit_id }); }} data-testid={`nav-audit-${insp.id}`}>
                        <Badge variant="secondary" className="text-xs cursor-pointer hover:bg-blue-100"><Link2 className="w-3 h-3 mr-1" />{t('inv.inspections.viewAudit')}</Badge>
                      </button>
                    ) : <Badge variant="secondary" className="text-xs"><Link2 className="w-3 h-3 mr-1" />{t('inv.inspections.linked')}</Badge>
                  ) : '-'}
                </TableCell>
                {isAdmin && (
                  <TableCell>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(insp)} data-testid={`edit-inspection-${insp.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(insp.id)} data-testid={`delete-inspection-${insp.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-inspection-dialog">
          <DialogHeader><DialogTitle><ClipboardCheck className="w-4 h-4 inline mr-1" />{t('inv.inspections.log')}</DialogTitle><DialogDescription className="sr-only">{t('inv.inspections.log')}</DialogDescription></DialogHeader>
          {renderForm(handleAdd, t('inv.inspections.log'), t('common.save'))}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-inspection-dialog">
          <DialogHeader><DialogTitle><Pencil className="w-4 h-4 inline mr-1" />{t('inv.inspections.edit')}</DialogTitle><DialogDescription className="sr-only">{t('inv.inspections.edit')}</DialogDescription></DialogHeader>
          {renderForm(handleEdit, t('inv.inspections.edit'), t('inv.common.update'))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetInspections;
